// Copyright 2026 Zibby Lab. Apache-2.0.
//
// AgentOpsToggle — per-app on/off control for the in-container agent-ops
// scheduler. Shared by the cloud-app Settings tab and the solo-app detail
// body so the control reads + behaves identically across deploy modes.
//
// Styled to match the "Warm workflow" section (title · short hint · current
// state · a single action button) rather than an inline switch.
//
// `onToggle(next)` performs the real flip (async; the caller hits
// Apps.setAgentOps / setSoloAgentOps, then refetches). It MUST throw on
// failure so the optimistic state reverts and the caller's toast explains
// why. The app itself keeps running when turned off — only agent-ops'
// autonomous scheduled runs stop; `zibby app run` still works.
//
// ENABLE gating: the scheduler is 100% BYOK (it can't run without a Claude
// credential), so turning it ON has to confirm a token exists first. When
// `onRequestEnable` is provided, the ENABLE path defers entirely to the
// parent — it runs the per-project credential check → confirm flow and
// drives `enabled` itself only after the real enable succeeds (the
// `useEffect` below re-syncs the switch). We DON'T optimistically flip on
// in that case, so a cancelled confirm / closed credential modal leaves the
// switch OFF. Backward-compat: absent `onRequestEnable`, ENABLE behaves like
// DISABLE (optimistic flip + `onToggle(true)`).

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { IonIcon } from '@ionic/react';
import { flashOutline } from 'ionicons/icons';

import DangerButton from '../DangerButton/DangerButton';
import './AgentOpsToggle.css';

export default function AgentOpsToggle({ enabled, onToggle, onRequestEnable, separated = true }) {
  const [on, setOn] = useState(enabled !== false);
  const [submitting, setSubmitting] = useState(false);

  // Keep in sync when the parent refetches the app (e.g. after the flip
  // resolves, or a poll updates the row).
  useEffect(() => { setOn(enabled !== false); }, [enabled]);

  const flip = async () => {
    if (submitting) return;
    const next = !on;
    // ENABLE with a gate (onRequestEnable provided): defer to the parent and
    // do NOT optimistically flip on — it runs the BYOK token gate and updates
    // `enabled` only after the real enable succeeds (the useEffect above then
    // re-syncs the switch). A cancelled confirm / closed cred modal leaves the
    // switch OFF.
    if (next && onRequestEnable) {
      setSubmitting(true);
      try {
        await onRequestEnable();
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setOn(next); // optimistic (DISABLE, or ENABLE without a gate)
    setSubmitting(true);
    try {
      await onToggle?.(next);
    } catch {
      setOn(!next); // revert — the caller surfaces the error toast
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`agentops-section${separated ? ' agentops-section--separated' : ''}`}>
      <h3 className="agentops-title">Agent automation</h3>
      <p className="agentops-hint">
        Runs agent-ops scheduled health checks + upgrades inside the app. Needs a
        BYOK Claude token; the app keeps running and <code>zibby app run</code>{' '}
        works either way.
      </p>
      <p className="agentops-state">
        Current state: <strong>{on ? 'On' : 'Off'}</strong>
      </p>
      {on ? (
        <DangerButton icon={flashOutline} onClick={flip} disabled={submitting}>
          {submitting ? 'Disabling…' : 'Disable agent automation'}
        </DangerButton>
      ) : (
        <button
          type="button"
          className="agentops-enable-btn"
          onClick={flip}
          disabled={submitting}
        >
          <IonIcon icon={flashOutline} aria-hidden="true" />
          {submitting ? 'Enabling…' : 'Enable agent automation'}
        </button>
      )}
    </div>
  );
}

AgentOpsToggle.propTypes = {
  enabled: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  // Optional ENABLE gate. When present, turning ON calls this instead of the
  // optimistic onToggle(true) path; the parent drives `enabled` after the
  // gated enable succeeds. Absent → ENABLE behaves like today.
  onRequestEnable: PropTypes.func,
  separated: PropTypes.bool,
};
